
import React from 'react';
import { Mic, MicOff, Loader2, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';

interface SpeechRecognitionProps {
  isListening: boolean;
  isProcessing: boolean;
  isSpeaking: boolean;
  isDisabled: boolean;
  onStart: () => void;
  onStop: () => void;
}

const SpeechRecognition: React.FC<SpeechRecognitionProps> = ({
  isListening,
  isProcessing,
  isSpeaking,
  isDisabled,
  onStart,
  onStop,
}) => {
  const isMobile = useIsMobile();
  
  return (
    <div className="flex flex-col items-center space-y-3">
      <div className="relative">
        {/* Recording animation ring - smaller on mobile */}
        {isListening && !isProcessing && !isSpeaking && (
          <div className={`absolute ${isMobile ? '-inset-0.5' : '-inset-1'} rounded-full animate-pulse bg-red-500/30`} />
        )}
        
        {/* Recording waves animation - adjusted for mobile */}
        {isListening && !isProcessing && !isSpeaking && (
          <div className={`absolute ${isMobile ? '-inset-3' : '-inset-4'}`}>
            <div className="absolute inset-0 flex items-center justify-center">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-full h-full rounded-full border-2 border-red-500 animate-[soundWave_1.5s_ease-out_infinite]"
                  style={{
                    animationDelay: `${i * 0.2}s`,
                    scale: `${1 + i * (isMobile ? 0.15 : 0.2)}`,
                    opacity: 0,
                  }}
                />
              ))}
            </div>
          </div>
        )}

        <Button
          onClick={isListening ? onStop : onStart}
          className={`${
            isMobile ? 'w-12 h-12' : 'w-14 h-14'
          } rounded-full transition-all duration-200 relative z-10 ${
            isListening && !isProcessing && !isSpeaking
              ? 'bg-red-500 hover:bg-red-600 border-2 border-red-600' 
              : 'bg-[#2E7D32] hover:bg-[#4CAF50]'
          } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={isDisabled}
        >
          {isProcessing ? (
            <div className="relative">
              <Loader2 className={`${isMobile ? 'h-5 w-5' : 'h-6 w-6'} animate-spin`} />
              {/* Typing indicator - smaller on mobile */}
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex gap-1">
                <div className="w-1.5 h-1.5 bg-[#2E7D32] rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
                <div className="w-1.5 h-1.5 bg-[#2E7D32] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                <div className="w-1.5 h-1.5 bg-[#2E7D32] rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
              </div>
            </div>
          ) : isSpeaking ? (
            <Volume2 className={`${isMobile ? 'h-5 w-5' : 'h-6 w-6'} animate-pulse`} />
          ) : isListening ? (
            <MicOff className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
          ) : (
            <Mic className={`${isMobile ? 'h-5 w-5' : 'h-6 w-6'}`} />
          )}
        </Button>
      </div>

      <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-500 text-center`}>
        {isProcessing 
          ? 'Procesando pregunta...' 
          : isSpeaking
            ? 'Escuchando respuesta...'
            : isListening 
              ? 'Haga clic para detener' 
              : 'Oprime boton verde para hacer tu pregunta'}
      </p>
    </div>
  );
};

export default SpeechRecognition;
